import { useState, type JSX } from 'react'
import { ApiError, api, refusalReason, shortDate, type ChannelConsent, type Contact } from '../lib/api'
import { Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'

// How one vendor contact may be reached: by email, SMS and WhatsApp, which one
// they prefer, whether they agreed to WhatsApp, and any channel they asked not
// to be written to on. Collapsed by default, because on most days nobody needs
// it, and on the day somebody does it answers "why did they not hear from us".

const CHANNELS: { code: 'EMAIL' | 'SMS' | 'WHATSAPP'; label: string }[] = [
  { code: 'EMAIL', label: 'Email' },
  { code: 'SMS', label: 'SMS' },
  { code: 'WHATSAPP', label: 'WhatsApp' },
]

type Form = { channel: string; kind: 'optIn' | 'object' } | null

export function ContactChannels({
  vendorId,
  contact,
  consents,
  canEdit,
  onSaved,
}: {
  vendorId: string
  contact: Contact
  consents: ChannelConsent[]
  canEdit: boolean
  onSaved: () => void
}): JSX.Element {
  const [form, setForm] = useState<Form>(null)
  const [source, setSource] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState('')

  const byChannel = (code: string) => consents.find((c) => c.channel === code)
  const hasAddress = (code: string) => (code === 'EMAIL' ? Boolean(contact.email) : Boolean(contact.mobileE164))

  async function save(channel: string, body: Record<string, unknown>) {
    setBusy(true)
    setSaid('')
    try {
      await api.setContactChannel(vendorId, contact.id, channel, body)
      setForm(null)
      setSource('')
      setNote('')
      onSaved()
    } catch (err) {
      setSaid(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <details className="contact-channels">
      <summary>How this contact may be reached</summary>
      <p className="field-note">
        Email and SMS rely on the contract, WhatsApp needs their agreement{' '}
        <Toggletip label="Which channels may be used">
          Messages about the assessment rely on the contract with their organisation. WhatsApp also needs their
          agreement, and a channel they objected to is never used.
        </Toggletip>
      </p>
      <ul className="p1-inline-list">
        {CHANNELS.map(({ code, label }) => {
          const c = byChannel(code)
          const objected = Boolean(c?.objectedAt)
          return (
            <li key={code}>
              <strong>{label}</strong>{' '}
              {c?.preferred && <Status tone="brand">Preferred</Status>}{' '}
              {!hasAddress(code) ? (
                <Status tone="plain">{code === 'EMAIL' ? 'No address' : 'No number'}</Status>
              ) : objected ? (
                <>
                  <Status tone="crit">Objected</Status>{' '}
                  <span className="muted">
                    {shortDate(c?.objectedAt)} ({c?.objectionSource}){c?.objectionNote ? ': ' + c.objectionNote : ''}
                  </span>
                </>
              ) : code === 'WHATSAPP' ? (
                c?.optedInAt ? (
                  <>
                    <Status tone="ok">Agreed</Status>{' '}
                    <span className="muted">
                      {shortDate(c.optedInAt)} ({c.optInSource})
                    </span>
                  </>
                ) : (
                  <>
                    <Status tone="neutral">Not agreed</Status>{' '}
                    <Toggletip label="Why nothing is sent on WhatsApp">
                      No agreement is recorded, so nothing is sent on WhatsApp.
                    </Toggletip>
                  </>
                )
              ) : (
                <Status tone="ok">Allowed</Status>
              )}
              {canEdit && hasAddress(code) && (
                <span className="chiprow">
                  {!c?.preferred && !objected && (
                    <button
                      type="button"
                      className="linkish"
                      data-action="tprm.vendor,vendor-record.a01"
                      disabled={busy}
                      onClick={() => save(code, { preferred: true })}
                    >
                      Try this first
                    </button>
                  )}
                  {code === 'WHATSAPP' && !objected && !c?.optedInAt && (
                    <button type="button" className="linkish" disabled={busy} onClick={() => setForm({ channel: code, kind: 'optIn' })}>
                      Record agreement
                    </button>
                  )}
                  {code === 'WHATSAPP' && c?.optedInAt && (
                    <button
                      type="button"
                      className="linkish"
                      data-action="tprm.vendor,vendor-record.a01"
                      disabled={busy}
                      onClick={() => save(code, { optIn: false })}
                    >
                      Withdraw agreement
                    </button>
                  )}
                  {objected ? (
                    <button
                      type="button"
                      className="linkish"
                      data-action="tprm.vendor,vendor-record.a01"
                      disabled={busy}
                      onClick={() => save(code, { object: false })}
                    >
                      Withdraw objection
                    </button>
                  ) : (
                    <button type="button" className="linkish" disabled={busy} onClick={() => setForm({ channel: code, kind: 'object' })}>
                      Record objection
                    </button>
                  )}
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {!canEdit && (
        <p className="self-note">
          <Status tone="neutral">Read only</Status>{' '}
          <ReasonTip label="Why this is read only" text={refusalReason('vendor.edit')} />
        </p>
      )}
      {form && (
        <form
          className="reject-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.kind === 'optIn') save(form.channel, { optIn: true, optInSource: source })
            else save(form.channel, { object: true, objectionSource: source, objectionNote: note })
          }}
        >
          <label className="field-label" htmlFor={'channel-source-' + contact.id}>
            {form.kind === 'optIn' ? 'Where the agreement came from' : 'How they objected'}
          </label>
          <input
            id={'channel-source-' + contact.id}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder={form.kind === 'optIn' ? 'Signed onboarding form, clause 9' : 'Replied to the reminder asking us to stop'}
          />
          {form.kind === 'object' && (
            <>
              <label className="field-label" htmlFor={'channel-note-' + contact.id}>
                Note
              </label>
              <input id={'channel-note-' + contact.id} value={note} onChange={(e) => setNote(e.target.value)} />
            </>
          )}
          <div className="exc-actions">
            <button type="submit" data-action="tprm.vendor,vendor-record.a01" disabled={busy || !source.trim()}>
              Save
            </button>
            <button type="button" className="linkish" onClick={() => setForm(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {said && <p className="note stop">{said}</p>}
    </details>
  )
}
